import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { AppConfigService } from './app-config.service';

export interface AuthApiResponse {
  empID: number;
  officeID: string;
  firstName: string;
  lastName: string;
  isActive: boolean;
  email: string;
  contact: number;
  officeCode: string;
  departmentConnect: Array<{
    departmentID: number;
    roleID: number;
  }>;
  reportingTo: unknown[];
}

export interface AuthResult {
  success: boolean;
  message: string;
}

export interface SsoLinkRecord {
  token: string;
  url: string;
  createdAt: number;
  expiresAt: number;
}

interface SsoPayload {
  issuedAt: number;
  expiresAt: number;
  nonce: string;
  mode: 'super-admin';
}

const SESSION_STORAGE_KEY = 'innowia-auth-user';
const SESSION_LOGIN_ID_KEY = 'innowia-auth-login-id';
const SSO_LINK_STORAGE_KEY = 'innowia-sso-link-record';
const SSO_LINK_DURATION_MS = 7 * 24 * 60 * 60 * 1000;
export const SUPER_ADMIN_LOGIN_ID = 'super-admin';
const SUPER_ADMIN_USER: AuthApiResponse = {
  empID: 0,
  officeID: 'SUPER-ADMIN',
  firstName: 'Super',
  lastName: 'Admin',
  isActive: true,
  email: 'super.admin@innowia.local',
  contact: 0,
  officeCode: 'SA-001',
  departmentConnect: [
    {
      departmentID: 7,
      roleID: 1
    }
  ],
  reportingTo: []
};

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly appConfigService = inject(AppConfigService);
  private readonly storedUser = this.getStoredUser();
  private readonly storedLoginId = this.getStoredLoginId();

  readonly authenticatedUser = signal<AuthApiResponse | null>(this.storedUser);
  readonly authenticatedEmail = signal<string | null>(this.storedUser?.email ?? null);
  readonly authenticatedLoginId = signal<string | null>(this.storedLoginId);

  async signIn(loginId: string, password: string): Promise<AuthResult> {
    const normalizedLoginId = loginId.trim();
    const normalizedPassword = password.trim();

    if (!normalizedLoginId || !normalizedPassword) {
      return {
        success: false,
        message: 'Enter both user id and password.'
      };
    }

    try {
      const response = await this.authenticateRequest(normalizedLoginId, normalizedPassword);

      if (!response) {
        return {
          success: false,
          message: 'Authentication failed. No user data was returned.'
        };
      }

      if (!response.isActive) {
        return {
          success: false,
          message: 'This user is inactive and cannot access the portal.'
        };
      }

      this.applyAuthenticatedSession(response, normalizedLoginId);

      return {
        success: true,
        message: `Welcome ${response.firstName} ${response.lastName}`.trim()
      };
    } catch (error) {
      console.error('Authentication request failed', error);
      return {
        success: false,
        message:
          'Unable to authenticate with the HR service. Please verify credentials.'
      };
    }
  }

  signOut(): void {
    this.authenticatedUser.set(null);
    this.authenticatedEmail.set(null);
    this.authenticatedLoginId.set(null);
    window.localStorage.removeItem(SESSION_STORAGE_KEY);
    window.localStorage.removeItem(SESSION_LOGIN_ID_KEY);
  }

  isAuthenticated(): boolean {
    const user = this.authenticatedUser();
    return Boolean(user && user.isActive);
  }

  canAccessAdminControler(): boolean {
    const user = this.authenticatedUser();

    if (!user?.isActive) {
      return false;
    }

    return user.departmentConnect.some(
      (assignment) => assignment.departmentID === 7 && assignment.roleID === 1
    );
  }

  isSsoSuperAdmin(): boolean {
    return this.authenticatedLoginId() === SUPER_ADMIN_LOGIN_ID;
  }

  getSsoLinkRecord(): SsoLinkRecord | null {
    if (typeof window === 'undefined') {
      return null;
    }

    const storedValue = window.localStorage.getItem(SSO_LINK_STORAGE_KEY);

    if (!storedValue) {
      return null;
    }

    try {
      return JSON.parse(storedValue) as SsoLinkRecord;
    } catch {
      window.localStorage.removeItem(SSO_LINK_STORAGE_KEY);
      return null;
    }
  }

  createSsoLink(): SsoLinkRecord | null {
    if (typeof window === 'undefined') {
      return null;
    }

    const createdAt = Date.now();
    const expiresAt = createdAt + SSO_LINK_DURATION_MS;
    const payload: SsoPayload = {
      issuedAt: createdAt,
      expiresAt,
      mode: 'super-admin',
      nonce:
        typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
          ? crypto.randomUUID()
          : `${createdAt}-${Math.random().toString(36).slice(2)}`
    };
    const token = this.base64UrlEncode(JSON.stringify(payload));
    const record: SsoLinkRecord = {
      token,
      url: `${window.location.origin}/sso-access?token=${encodeURIComponent(token)}`,
      createdAt,
      expiresAt
    };

    window.localStorage.setItem(SSO_LINK_STORAGE_KEY, JSON.stringify(record));
    return record;
  }

  async signInWithSsoToken(token: string): Promise<AuthResult> {
    const normalizedToken = token.trim();

    if (!normalizedToken) {
      return {
        success: false,
        message: 'SSO access token is missing.'
      };
    }

    try {
      const payload = JSON.parse(this.base64UrlDecode(normalizedToken)) as SsoPayload;

      if (!payload?.mode || payload.mode !== 'super-admin') {
        return {
          success: false,
          message: 'SSO access token is invalid.'
        };
      }

      if (Date.now() > payload.expiresAt) {
        return {
          success: false,
          message: 'SSO access link has expired. Please activate it again.'
        };
      }

      this.applyAuthenticatedSession(SUPER_ADMIN_USER, SUPER_ADMIN_LOGIN_ID);
      return {
        success: true,
        message: `Welcome ${SUPER_ADMIN_USER.firstName} ${SUPER_ADMIN_USER.lastName}`.trim()
      };
    } catch {
      return {
        success: false,
        message: 'SSO access token is invalid.'
      };
    }
  }

  async verifyCurrentPassword(password: string): Promise<boolean> {
    const user = this.authenticatedUser();
    const normalizedPassword = password.trim();

    if (!user || !normalizedPassword) {
      console.debug('[ChangePassword] Verification skipped: missing user or password.');
      return false;
    }

    const candidateLoginIds = Array.from(
      new Set(
        [
          this.authenticatedLoginId(),
          user.officeID,
          user.email,
          String(user.empID)
        ]
          .map((value) => value?.trim())
          .filter((value): value is string => Boolean(value))
      )
    );

    console.debug('[ChangePassword] Candidate login IDs for verification:', candidateLoginIds);

    try {
      for (const loginId of candidateLoginIds) {
        try {
          console.debug(`[ChangePassword] Verifying current password with loginId="${loginId}"`);
          const response = await this.authenticateRequest(loginId, normalizedPassword);

          if (response && response.isActive) {
            console.debug(`[ChangePassword] Verification succeeded with loginId="${loginId}"`);
            this.authenticatedLoginId.set(loginId);
            window.localStorage.setItem(SESSION_LOGIN_ID_KEY, loginId);
            return true;
          }
        } catch {
          console.debug(`[ChangePassword] Verification failed with loginId="${loginId}"`);
          continue;
        }
      }

      console.debug('[ChangePassword] Verification failed for all candidate login IDs.');
      return false;
    } catch (error) {
      console.error('Current password verification failed', error);
      return false;
    }
  }

  async resetPassword(newPassword: string): Promise<AuthResult> {
    const loginId = this.authenticatedLoginId();
    const normalizedPassword = newPassword.trim();

    if (!loginId || !normalizedPassword) {
      return {
        success: false,
        message: 'Unable to identify the signed-in user for password reset.'
      };
    }

    try {
      const response = await firstValueFrom(
        this.http.post(
          this.appConfigService.resetPasswordApiUrl,
          null,
          {
            params: new HttpParams()
              .set('loginId', loginId)
              .set('psw', normalizedPassword),
            responseType: 'text'
          }
        )
      );

      const normalizedResponse = String(response ?? '')
        .trim()
        .toLowerCase();

      if (normalizedResponse !== 'true') {
        return {
          success: false,
          message: 'Password change failed. Please verify your details and try again.'
        };
      }

      return {
        success: true,
        message: 'Password changed successfully.'
      };
    } catch (error) {
      console.error('Password reset request failed', error);
      return {
        success: false,
        message: 'Unable to change password right now. Please try again.'
      };
    }
  }

  private authenticateRequest(loginId: string, password: string): Promise<AuthApiResponse> {
    return firstValueFrom(
      this.http.post<AuthApiResponse>(
        this.appConfigService.authApiUrl,
        null,
        {
          params: new HttpParams()
            .set('loginId', loginId)
            .set('psw', password)
        }
      )
    );
  }

  private applyAuthenticatedSession(user: AuthApiResponse, loginId: string): void {
    this.authenticatedUser.set(user);
    this.authenticatedEmail.set(user.email ?? null);
    this.authenticatedLoginId.set(loginId);
    window.localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(user));
    window.localStorage.setItem(SESSION_LOGIN_ID_KEY, loginId);
  }

  private base64UrlEncode(value: string): string {
    const bytes = new TextEncoder().encode(value);
    let binary = '';

    bytes.forEach((byte) => {
      binary += String.fromCharCode(byte);
    });

    return window
      .btoa(binary)
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/g, '');
  }

  private base64UrlDecode(value: string): string {
    const normalizedValue = value.replace(/-/g, '+').replace(/_/g, '/');
    const paddedValue =
      normalizedValue.length % 4 === 0
        ? normalizedValue
        : normalizedValue + '='.repeat(4 - (normalizedValue.length % 4));
    const binary = window.atob(paddedValue);
    const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));

    return new TextDecoder().decode(bytes);
  }

  private getStoredUser(): AuthApiResponse | null {
    if (typeof window === 'undefined') {
      return null;
    }

    const storedValue = window.localStorage.getItem(SESSION_STORAGE_KEY);

    if (!storedValue) {
      return null;
    }

    try {
      return JSON.parse(storedValue) as AuthApiResponse;
    } catch {
      window.localStorage.removeItem(SESSION_STORAGE_KEY);
      return null;
    }
  }

  private getStoredLoginId(): string | null {
    if (typeof window === 'undefined') {
      return null;
    }

    return window.localStorage.getItem(SESSION_LOGIN_ID_KEY);
  }
}

